import React, { useEffect, useRef, useState } from "react"
import { AuthCard, AuthLayout } from "components/views/login/Shared"
import { useFormContext } from "react-hook-form"
import { CompleteFormInput } from "components/views/atoms/forms/FormInput"
import { Spacer } from "components/views/atoms/Atoms"
import { Body } from "components/views/atoms/Bodies"
import ErrorMessageMarkdown from "components/views/login/ErrorMessageMarkdown"
import { useHistory } from "react-router"
import { LeftBackArrowImage } from "../views/fle/Molecules"
import { ButtonPrimary } from "../views/atoms/buttons/ButtonPrimary"
import L10n from "../../lib/carrot-l10n"
import { useSignUpFlow } from "../context/signup/SignUpFlowContext"
import { useSignUpGetHelp } from "./context/SignUpGetHelpContext"
import moment from "moment"
import SignUpGetHelpSuccess from "../../pages/signup-get-help-success"
import { DateOfBirthWithLabels } from "../views/modal/DateOfBirthWithLabels"
import styled from "styled-components"
import { H1 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const EmployerNameInput = ({ enabled }) => {
  const { register, errors } = useFormContext()
  return (
    <CompleteFormInput
      readOnly={!enabled}
      ref={register({ required: "Employer name is required" })}
      errors={errors}
      showValidation={false}
      id="employer"
      name="employer"
      label="Employer Name"
      type="employerName"
      showAsterisk
      autoFocus
    />
  )
}

const EmailInput = ({ enabled }) => {
  const { register, errors } = useFormContext()
  const { email } = useSignUpFlow()
  return (
    <CompleteFormInput
      readOnly={!enabled}
      ref={register({ required: "Email is required" })}
      errors={errors}
      showValidation={false}
      defaultValue={email}
      id="email"
      name="email"
      label="Email"
      type="email"
      showAsterisk
    />
  )
}

const FirstNameInput = ({ enabled }) => {
  const { register, errors } = useFormContext()
  const { firstName } = useSignUpFlow()

  return (
    <CompleteFormInput
      readOnly={!enabled}
      ref={register({ required: "First Name is required" })}
      errors={errors}
      showValidation={false}
      defaultValue={firstName}
      id="firstName"
      name="firstName"
      label="First Name"
      type="firstName"
      showAsterisk
    />
  )
}

const LastNameInput = ({ enabled }) => {
  const { register, errors } = useFormContext()
  const { lastName } = useSignUpFlow()

  return (
    <CompleteFormInput
      readOnly={!enabled}
      ref={register({ required: "Last name is required" })}
      errors={errors}
      showValidation={false}
      defaultValue={lastName}
      id="lastName"
      name="lastName"
      label="Last Name"
      type="lastName"
      showAsterisk
    />
  )
}

function GetHelpBackArrow() {
  const history = useHistory()
  const { email } = useSignUpFlow()
  return (
    <div
      className="pointer bg-carrot-orange-01 br-100 flex items-center justify-center"
      style={{
        height: "2rem",
        width: "2rem"
      }}
      onClick={() =>
        email ? history.push(`/signup-find-account?email=${encodeURIComponent(email)}`) : history.push("/signup")
      }
    >
      <LeftBackArrowImage />
    </div>
  )
}

function SignupGetHelpHeader() {
  const { errorMessage } = useSignUpGetHelp()
  const { multipleEmployeesFound } = useSignUpFlow()
  return (
    <>
      <GetHelpBackArrow />
      <Spacer height={1.5} />
      <>
        <H1>{l`Get help`}</H1>
        <Spacer height={0.5} />
        {errorMessage ? (
          <ErrorMessageMarkdown align={"tl"} source={errorMessage} />
        ) : multipleEmployeesFound ? (
          <Body color="black-80">{l`We’re having trouble finding your account. Please provide the information 
          below and our Care Team will reach out with next steps.`}</Body>
        ) : (
          <Body color="black-80">{l`Our Care Team will use this information to look for your account.`}</Body>
        )}
      </>
    </>
  )
}

const FindAccountButton = ({ submitWithDob, showSubmittingLabel }) => (
  <div className="flex flex-auto justify-center">
    <ButtonPrimary enabled={!showSubmittingLabel} onClick={submitWithDob}>
      {showSubmittingLabel ? l`Sending...` : l`Find account`}
    </ButtonPrimary>
  </div>
)

const AuthCardContainer = styled.div`
  margin-top: 6rem;
`

const SignUpGetHelpForm = () => {
  const { shake, inputsEnabled, onSubmit, success, showSubmittingLabel } = useSignUpGetHelp()
  const dateOfBirthRef = useRef()
  const [dateOfBirthFormValue, setDateOfBirth] = useState(null)
  const [validDateOfBirth, setValidDateOfBirth] = useState(true)
  const { dateOfBirth } = useSignUpFlow()

  function isDateOfBirthValid() {
    // @ts-ignore
    const dobInputIsValid = dateOfBirthRef.current.validate()
    setValidDateOfBirth(dobInputIsValid)
    return dobInputIsValid
  }

  const submitWithDob = () => {
    if (!isDateOfBirthValid()) return
    // @ts-ignore
    setDateOfBirth(dateOfBirthRef.current.getMoment())
    // @ts-ignore
    const dobVal = dateOfBirthRef.current.getMoment().format("YYYY-MM-DD")
    onSubmit(dobVal)
  }

  useEffect(() => {
    if (dateOfBirth) {
      setDateOfBirth(moment(dateOfBirth))
    }
  }, [dateOfBirth])

  return success ? (
    <SignUpGetHelpSuccess />
  ) : (
    <AuthCardContainer>
      <AuthCard shake={shake} onSubmit={submitWithDob}>
        <Spacer height={1.5} />
        <SignupGetHelpHeader />
        <Spacer height={2} />
        <EmployerNameInput enabled={inputsEnabled} />
        <Spacer height={2} />
        <EmailInput enabled={inputsEnabled} />
        <Spacer height={2} />
        <div className="flex flex-row">
          <div className="flex-auto" style={{ flexBasis: 0 }}>
            <FirstNameInput enabled={inputsEnabled} />
          </div>
          <Spacer width={1} />
          <div className="flex-auto" style={{ flexBasis: 0 }}>
            <LastNameInput enabled={inputsEnabled} />
          </div>
        </div>
        <Spacer height={2} />
        <DateOfBirthWithLabels
          showAsterisk
          {...{ dateOfBirthRef, validDateOfBirth }}
          dateOfBirth={dateOfBirthFormValue}
        />
        <Spacer height={2} />
        <FindAccountButton {...{ submitWithDob, showSubmittingLabel }} />
        <Spacer height={2} />
      </AuthCard>
    </AuthCardContainer>
  )
}

const SignUpGetHelpPage = () => {
  return (
    <AuthLayout title={l`client.signUp.short`}>
      <SignUpGetHelpForm />
    </AuthLayout>
  )
}

export default SignUpGetHelpPage
